const fontSizeMedium = 'var(--fontSizeMedium, 0.875rem)'
import { useEffect } from 'react'
import PropTypes from 'prop-types'
import styled from 'astroturf/react'
import { useDispatch, useSelector } from 'react-redux'
import { httpRequest } from '@calendly/http-tools'
import { MinimalButton } from '@calendly/ui/components/minimal-button'

import ErrorMessage from 'components/form/error_message'
import PayButton from 'components/payment/paypal/pay_button'
import { setPaymentData, setPaymentError, resetPayment } from 'actions/payment'
import { eventTypeSelector } from 'root-api'
import { useI18n } from 'locales'
import _LogoContainer from "paypal_form-LogoContainer.module.css!=!astroturf/inline-loader?style=1!/home/runner/work/booking/booking/frontend/components/enter_details/payment/paypal_form.js?styleId=LogoContainer";
import _PaymentLogo from "paypal_form-PaymentLogo.module.css!=!astroturf/inline-loader?style=1!/home/runner/work/booking/booking/frontend/components/enter_details/payment/paypal_form.js?styleId=PaymentLogo";
import _BillingDetails from "paypal_form-BillingDetails.module.css!=!astroturf/inline-loader?style=1!/home/runner/work/booking/booking/frontend/components/enter_details/payment/paypal_form.js?styleId=BillingDetails";
import _ErrorContainer from "paypal_form-ErrorContainer.module.css!=!astroturf/inline-loader?style=1!/home/runner/work/booking/booking/frontend/components/enter_details/payment/paypal_form.js?styleId=ErrorContainer";


// "Error: /v2/checkout/orders returned status: 400 (Corr ID: 32bc98f3b2754, 32bc98f3b2754)"
const parseErrorCode = (error) => {
  if (!(error instanceof Error)) return undefined
  const found = error.message.match(/status: (\d+)/)
  if (!found) return undefined
  return found[1]
}

const getErrorMessage = (something, i18n) => {
  const errorCode = parseErrorCode(something)
  let message = i18n.t('booking.error.default')
  if (errorCode) message += ` (${errorCode})`
  return message
}

const PaymentButton = ({ ordersPath, paypalClientId, paypalEnv, isPpcp, onPaypalAuthorized }) => {
  const { i18n } = useI18n()
  const dispatch = useDispatch()
  const {
    uuid: eventTypeUuid,
    payment_method: {
      provider_locale: providerLocale,
    }
  } = useSelector(eventTypeSelector)

  const onError = (error) => dispatch(setPaymentError(getErrorMessage(error, i18n)))

  const onCreateOrder = async () => {
    dispatch(setPaymentError(null))
    const [_, data] = await httpRequest({
      url: ordersPath,
      method: 'POST',
      data: {
        event_type_uuid: eventTypeUuid
      }
    })

    const { id } = data
    return id || null
  }

  const onApprove = async (orderData) => {
    const [success, data] = await httpRequest({
      url: `${ordersPath}/${eventTypeUuid}/${orderData.orderID}`,
      method: 'GET'
    })

    if (!success) {
      const { error } = data
      dispatch(setPaymentError(error))
      return null
    }

    dispatch(setPaymentData(data))
    if (onPaypalAuthorized) {
      window.setTimeout(() => onPaypalAuthorized(), 0)
    }
    return data
  }

  return (
    <PayButton
      locale={providerLocale}
      onCreateOrder={onCreateOrder}
      onApprove={onApprove}
      onError={onError}
      paypalClientId={paypalClientId}
      paypalEnv={paypalEnv}
      isPpcp={isPpcp}
    />
  )
}

PaymentButton.propTypes = {
  ordersPath: PropTypes.string.isRequired,
  paypalClientId: PropTypes.string.isRequired,
  paypalEnv: PropTypes.string.isRequired,
  isPpcp: PropTypes.bool,
  onPaypalAuthorized: PropTypes.func
}

const ShippingInfo = () => {
  const address = useSelector((state) => {
    const { data } = state.scheduling.payment

    return data.shipping_address
  })

  if (!address) return null
  const { address_line_1: line1, admin_area_2: area2, admin_area_1: area1, postal_code: code } = address

  return (
    <>
      <br />
      <div>{line1}</div>
      <div>{`${area2}, ${area1} ${code}`}</div>
    </>
  )
}

const LogoContainer = /*#__PURE__*/styled('div', null, {
  displayName: "LogoContainer",
  styles: _LogoContainer
})

const PaymentLogo = /*#__PURE__*/styled('div', null, {
  displayName: "PaymentLogo",
  styles: _PaymentLogo
})

const BillingDetails = /*#__PURE__*/styled('small', null, {
  displayName: "BillingDetails",
  styles: _BillingDetails,
  attrs: props => ({
    ...props,
    children: useI18n().i18n.t('booking.templates.payment.billing_details')
  })
})

// https://developer.paypal.com/docs/api/orders/v2/#definition-payer
const PaymentInfo = () => {
  const { i18n } = useI18n()
  const dispatch = useDispatch()

  const { data: { payer } } = useSelector((state) => state.scheduling.payment)
  const { first_name: firstName, last_name: lastName, email } = payer

  const onClickChange = () => dispatch(resetPayment())

  return (
    <>
      <LogoContainer>
        <PaymentLogo />
        <MinimalButton fontSize="medium" onClick={onClickChange}>
          {i18n.t('booking.templates.payment.change')}
        </MinimalButton>
      </LogoContainer>

      <div>
        <BillingDetails />
        <div>
          <div>
            {firstName}
            {' '}
            {lastName}
          </div>
          <div>{email}</div>
          <ShippingInfo />
        </div>
      </div>
    </>
  )
}

const ErrorContainer = /*#__PURE__*/styled('div', null, {
  displayName: "ErrorContainer",
  styles: _ErrorContainer
})

const PaypalForm = ({
  registerApi,
  ordersPath,
  paypalClientId,
  paypalEnv,
  isPpcp,
  onPaypalAuthorized
}) => {
  const { i18n } = useI18n()
  const dispatch = useDispatch()
  const { data, error } = useSelector((state) => state.scheduling.payment)

  useEffect(() => {
    registerApi({
      createPayment: () => {
        dispatch(setPaymentError(i18n.t('errors.booking.paypal.missing_payment_information')))
        return Promise.resolve(false)
      }
    })
  }, [dispatch, i18n, registerApi])

  return (
    <>
      {data ? <PaymentInfo /> : (
        <PaymentButton
          ordersPath={ordersPath}
          paypalClientId={paypalClientId}
          paypalEnv={paypalEnv}
          isPpcp={isPpcp}
          onPaypalAuthorized={onPaypalAuthorized}
        />
      )}
      {error && (
        <ErrorContainer>
          <ErrorMessage message={error} />
        </ErrorContainer>
      )}
    </>
  )
}

PaypalForm.propTypes = {
  registerApi: PropTypes.func.isRequired,
  ordersPath: PropTypes.string.isRequired,
  paypalClientId: PropTypes.string.isRequired,
  paypalEnv: PropTypes.string.isRequired,
  isPpcp: PropTypes.bool,
  onPaypalAuthorized: PropTypes.func
}

export default PaypalForm
